import { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { BsShieldX, BsTrash } from 'react-icons/bs';
import { HiPlus } from 'react-icons/hi2';

import Button from '#globalComponents/Button/Button';
import CollectionToolbar from '#globalComponents/CollectionView/CollectionToolbar';
import CollectionView from '#globalComponents/CollectionView/CollectionView';
import useCollectionSearch from '#globalComponents/CollectionView/useCollectionSearch';
import useCollectionViewMode from '#globalComponents/CollectionView/useCollectionViewMode';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';

import ContactListSearchOverlay from '../../components/ContactListSearchOverlay/ContactListSearchOverlay';
import CreateContactListModal from '../../components/CreateContactListModal/CreateContactListModal';
import { useDeleteContactList, useUpdateContactList } from '../../models/mutations';
import { useContactLists } from '../../models/queries';

const getSearchText = (list) => list.name;

const ContactListsOverviewPage = () => {
	const navigate = useNavigate();
	const { data: contactLists = [], isLoading } = useContactLists();
	const { mutateAsync: updateContactList } = useUpdateContactList();
	const deleteMutation = useDeleteContactList();

	const [viewMode, setViewMode] = useCollectionViewMode('contactLists');
	const { search, setSearch, filteredItems } = useCollectionSearch(contactLists, getSearchText);
	const [showCreate, setShowCreate] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);

	const items = useMemo(
		() =>
			filteredItems.map((list) => {
				const contactCount = list.contactCount || 0;
				const companyCount = list.companyCount || 0;
				return {
					id: list.id,
					title: list.name,
					onOpen: () => navigate(`/contact-lists/${list.id}`),
					onRename: (name) => updateContactList({ id: list.id, updates: { name } }),
					stats: [
						{ value: contactCount, label: contactCount === 1 ? 'Kontaktperson' : 'Kontaktpersonen' },
						{ value: companyCount, label: companyCount === 1 ? 'Firmenkontakt' : 'Firmenkontakte' },
					],
					date: { label: 'Erstellt am', value: list.createdAt },
					actions: [
						{
							key: 'delete',
							icon: BsTrash,
							label: 'Löschen',
							danger: true,
							onClick: () => setDeleteTarget(list),
						},
					],
				};
			}),
		[filteredItems, navigate, updateContactList],
	);

	const handleDelete = async () => {
		try {
			await deleteMutation.mutateAsync(deleteTarget.id);
			setDeleteTarget(null);
		} catch {
			// The global MutationCache shows the toast; keep the dialog open for a retry.
		}
	};

	return (
		<Page
			header={
				<PageHeader breadcrumbs={['Kontaktlisten']}>
					<ContactListSearchOverlay />
				</PageHeader>
			}
		>
			<CollectionToolbar
				search={search}
				onSearchChange={setSearch}
				searchPlaceholder="Liste suchen ..."
				viewMode={viewMode}
				onViewModeChange={setViewMode}
			>
				<Button variant="dark" shape="pill" size="sm" onClick={() => navigate('/contact-lists/blacklist')}>
					<BsShieldX className="h-3.5 w-3.5" />
					Blacklist
				</Button>
				<Button shape="pill" size="sm" onClick={() => setShowCreate(true)}>
					<HiPlus className="h-3.5 w-3.5" />
					Neue Liste
				</Button>
			</CollectionToolbar>

			<CollectionView
				items={items}
				viewMode={viewMode}
				isLoading={isLoading}
				emptyLabel={search ? `Keine Listen für „${search}“ gefunden.` : undefined}
				createLabel="neue Liste erstellen"
				onCreate={() => setShowCreate(true)}
			/>

			<CreateContactListModal isOpen={showCreate} onClose={() => setShowCreate(false)} />

			<ConfirmModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleDelete}
				title="Kontaktliste löschen"
				message={`Möchten Sie die Kontaktliste "${deleteTarget?.name}" wirklich löschen?`}
				confirmText="Liste löschen"
				cancelText="Abbrechen"
				isLoading={deleteMutation.isPending}
			/>
		</Page>
	);
};

export default ContactListsOverviewPage;
